import * as React from 'react';
import { connect } from 'react-redux';
import { setQuery, queryExpansionRequest } from '../../../app/Search/actions';
import { IState } from '../../../reducers';
import { Tooltip } from '../../Tooltip';
import { DefaultButton } from '../../ActionButton/ActionButton';
import { get_encoding } from 'tiktoken';
import { MessageBar, MessageBarType } from '@fluentui/react';

interface ISearchFieldProps {
  searchResults: any;
  setQuery: (e: any) => void;
  queryExpansionRequest: (query: string) => void;
  onSearch?: (query: string) => void;
}

interface ISearchFieldState {
  query: string;
  tokenCount: number;
  showWarning: boolean;
  toastMessage: string | null;
  toastType: MessageBarType | undefined;
}

class SearchField extends React.Component<ISearchFieldProps, ISearchFieldState> {
  state = {
    query: '',
    tokenCount: 0,
    showWarning: false,
    toastMessage: null,
    toastType: undefined,
  };

  private expansionDebounceTimer: any = null;

  constructor(props: ISearchFieldProps) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }


  // Token threshold for warning
  TOKEN_THRESHOLD = 8000;

  // Cap for suggesting new keywords (no suggestions if the search is longer than this number of words)
  MAX_WORDS_FOR_SUGGESTIONS = 15;

  getWordCount(text: string): number {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }

  showToast = (message: string, type: MessageBarType, duration = 10000) => {
    this.setState({
      toastMessage: message,
      toastType: type
    });
    setTimeout(() => {
      this.setState({ toastMessage: null });
    }, duration);
  };

  componentDidMount() {
    const query = this.props.searchResults?.query || '';
    const tokenCount = this.countTokens(query);

    this.setState({
      query,
      tokenCount,
      showWarning: tokenCount > this.TOKEN_THRESHOLD
    });
  }

  componentDidUpdate(prevProps: Readonly<ISearchFieldProps>, prevState: Readonly<ISearchFieldState>, snapshot?: any): void {
    if (prevProps.searchResults.query !== this.props.searchResults.query) {
      const query = this.props.searchResults?.query || '';
      const tokenCount = this.countTokens(query);

      this.setState({
        query,
        tokenCount,
        showWarning: tokenCount > this.TOKEN_THRESHOLD
      });
    }
  }

  countTokens(text: string): number {
    try {
      const enc = get_encoding('cl100k_base')
      const tokens = enc.encode(text);
      enc.free();
      return tokens.length;
    } catch (error) {
      // Fallback to simple counting if tiktoken fails
      console.error('Tiktoken error:', error);
      return text.trim() ? text.trim().split(/\s+/).length : 0;
    }
  }

  handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const query = e.target.value;
    const tokenCount = this.countTokens(query);
    const exceedsThreshold = tokenCount > this.TOKEN_THRESHOLD;

    this.setState({
      query,
      tokenCount,
      showWarning: exceedsThreshold
    });

    // Synchronize query with store so tab switching uses the latest text
    this.props.setQuery(query);

    // Show toast when token count exceeds threshold
    if (exceedsThreshold && !this.state.showWarning) {
      this.showToast(
        `Your query exceeds the maximum input length of 10 pages A4. Please shorten it`,
        MessageBarType.warning
      );
    }

    // Handle query expansion logic
    this.handleQueryExpansion(query);
  }

  handleQueryExpansion(query: string) {
    if (this.expansionDebounceTimer) {
      clearTimeout(this.expansionDebounceTimer);
    }

    const wordCount = this.getWordCount(query);
    if (wordCount > 0 && wordCount <= this.MAX_WORDS_FOR_SUGGESTIONS) {
      this.expansionDebounceTimer = setTimeout(() => {
        this.props.queryExpansionRequest(query);
      }, 800); // 800ms debounce
    }
  }

  handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!this.state.showWarning) {
        this.handleSubmit(e);
      } else {
        this.showToast(
          `Cannot search: Your query exceeds the maximum input length of 10 pages A4. Please shorten it`,
          MessageBarType.error
        );
      }
    }
  }

  handleSubmit(_e?: React.KeyboardEvent<HTMLTextAreaElement> | React.MouseEvent<HTMLButtonElement>) {
    if (this.state.showWarning) {
      this.showToast(
        `Your search contains ${this.state.tokenCount} tokens, which exceeds the recommended limit of ${this.TOKEN_THRESHOLD}. This may affect search quality.`,
        MessageBarType.warning
      );
    }

    // Trigger the search manually
    if (this.props.onSearch) {
      this.props.onSearch(this.state.query);
    }
  }

  render() {
    return (
      <div className="search-field">
        {this.state.toastMessage && (
          <div className="message-bar">
            <MessageBar
              messageBarType={this.state.toastType}
              isMultiline={false}
              onDismiss={() => this.setState({ toastMessage: null })}
              dismissButtonAriaLabel="Close"
            >
              {this.state.toastMessage}
            </MessageBar>
          </div>
        )}

        <label htmlFor="search-field">
          Enter  your idea description <Tooltip content={<TooltipContent />} />
        </label>

        <textarea
          id="search-field"
          className={this.props.searchResults.results.length > 0 ? 'shrink' : ''}
          rows={12}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          value={this.state.query}
        />
        <div className="search-button">
          <DefaultButton
            text="Search"
            handleClick={this.handleSubmit}
            disabled={this.state.showWarning}
          />
        </div>

        {this.props.searchResults.expansionSuggestions &&
          this.props.searchResults.expansionSuggestions.length > 0 &&
          this.getWordCount(this.state.query) <= this.MAX_WORDS_FOR_SUGGESTIONS && (
            <div className="expansion-suggestions">
              <p>Try these ideas:</p>
              <div className="suggestion-tags">
                {this.props.searchResults.expansionSuggestions.map((suggestion: string, index: number) => (
                  <button
                    key={index}
                    className="suggestion-tag"
                    onClick={() => {
                      const currentQuery = this.state.query.trim();
                      const newQuery = currentQuery
                        ? (currentQuery.endsWith(',') ? `${currentQuery} ${suggestion}` : `${currentQuery}, ${suggestion}`)
                        : suggestion;
                      this.setState({ query: newQuery, tokenCount: this.countTokens(newQuery) }, () => {
                        this.props.setQuery(newQuery);
                        this.handleQueryExpansion(newQuery);
                      });
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
      </div>
    );
  }
}

const mapStateToProps = ({ searchResults }: IState) => {
  return { searchResults };
};

const mapDispatchToProps = (dispatch: any) => ({
  setQuery: (query: string) => {
    dispatch(setQuery({ query }));
  },
  queryExpansionRequest: (query: string) => {
    dispatch(queryExpansionRequest(query));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchField);

const TooltipContent = () => {
  return (
    <p>
      Enter search text for your idea looking for funding and partners. Longer description leads to better and
      more comprehensive results. Examples of good search texts are project descriptions, company profiles,
      research topic abstracts or press releases. Press “Enter” to see the results once the text is written.
    </p>
  );
};
